import React, { useState } from 'react';
import { Alert, Box, InputLabel, Modal, RadioButton, Text, TextField } from '@hopskipdrive/react-components';

import type { DownloadReason, Recording } from '../../../api/services/Recordings';
import { requestRecordingDownload } from '../../../api/services/Recordings';
import { useTimezone } from '../../../hooks/useTimezone';
import { Button } from '../../../shared/components/Button/Button';
import { colorPalette } from '../../../shared/components/variables';
import { useToastStore } from '../../../shared/stores/toastStore';

interface DownloadRecordingModalProps {
  recording: Recording | null;
  onClose: () => void;
}

interface InfoFieldProps {
  label: string;
  value: string;
}

const InfoField: React.FC<InfoFieldProps> = ({ label, value }) => (
  <Box>
    <InputLabel>{label}</InputLabel>
    <Text>{value}</Text>
  </Box>
);

interface OptionCardProps {
  value: DownloadReason;
  label: string;
  subtext: string;
  selected: boolean;
  onClick: (value: DownloadReason) => void;
  children?: React.ReactNode;
}

const OptionCard: React.FC<OptionCardProps> = ({ value, label, subtext, selected, onClick, children }) => (
  <Box
    onClick={() => onClick(value)}
    sx={{
      border: `1px solid ${selected ? colorPalette.navy50 : '#cbd1d6'}`,
      borderRadius: '10px',
      padding: '12px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      cursor: 'pointer',
      width: '100%',
      transition: 'border-color 0.15s',
    }}
  >
    <RadioButton checked={selected} onChange={() => onClick(value)} onClick={(e) => e.stopPropagation()} />
    <Box flex={1}>
      <Text fontWeight={500} style={{ fontSize: '13px', lineHeight: '19.5px' }}>
        {label}
      </Text>
      {!children && (
        <Text textColor="secondaryText" style={{ fontSize: '13px', lineHeight: '20px' }}>
          {subtext}
        </Text>
      )}
      {children}
    </Box>
  </Box>
);

const DOWNLOAD_REASONS: { value: DownloadReason; label: string; subtext: string }[] = [
  {
    value: 'routine_review',
    label: 'Review or general monitoring',
    subtext: 'Quality assurance or scheduled compliance review',
  },
  {
    value: 'potential_incident_review',
    label: 'Incident investigation',
    subtext:
      'Related to a reported safety or conduct incident. Selecting this reason will alert HopSkipDrive to a potential incident and our team will investigate.',
  },
  {
    value: 'other',
    label: 'Something else',
    subtext: '',
  },
];

const triggerBrowserDownload = (url: string) => {
  const a = document.createElement('a');

  a.href = url;
  a.download = '';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const DownloadRecordingModal: React.FC<DownloadRecordingModalProps> = ({ recording, onClose }) => {
  const { addToast } = useToastStore();
  const { formatInTimezone } = useTimezone();
  const [reason, setReason] = useState<DownloadReason | null>(null);
  const [reasonDetail, setReasonDetail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDownloadDisabled = !reason || (reason === 'other' && !reasonDetail.trim()) || isSubmitting;

  const handleClose = () => {
    setReason(null);
    setReasonDetail('');
    onClose();
  };

  const handleDownload = async () => {
    if (!recording || !reason) {
      return;
    }

    setIsSubmitting(true);

    const response = await requestRecordingDownload(recording.deviceRecordingId, {
      reason,
      reasonDetail: reason === 'other' ? reasonDetail : undefined,
    });

    setIsSubmitting(false);

    if (response.type === 'success') {
      triggerBrowserDownload(response.data.downloadUrl);
      handleClose();
      addToast({ severity: 'success', message: 'Recording download has started.' });

      return;
    }

    const status = response.status;
    const errorMessage = response.error?.errors?.[0];

    if (status === 429) {
      addToast({
        severity: 'error',
        message: errorMessage ?? 'Download limit reached. You can request up to 10 recordings per hour.',
      });
      handleClose();

      return;
    }

    if (status === 404) {
      addToast({ severity: 'error', message: 'This recording is no longer available.' });

      return;
    }

    addToast({
      severity: 'error',
      message: errorMessage ?? 'Something went wrong. Please try again.',
    });
  };

  if (!recording) {
    return null;
  }

  return (
    <Modal
      open={!!recording}
      data-testid="download-recording-modal"
      PaperProps={{ style: { width: '584px', maxWidth: '100%' } }}
    >
      <Modal.Title>
        <Text variant="h6" fontWeight={700}>
          Download Recording
        </Text>
      </Modal.Title>

      <Modal.Content>
        <Box display="flex" flexDirection="column" gap="16px">
          {/* Ride Info Panel */}
          <Box
            sx={{
              border: `1px solid ${colorPalette.neutralVariant90}`,
              borderRadius: '20px',
              padding: '16px 24px',
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', rowGap: '16px', columnGap: '12px' }}>
              <InfoField label="Rider" value={recording.riderNames.join(', ')} />
              <InfoField label="CareDriver" value={recording.driverName} />
              <InfoField
                label="Start date and time"
                value={formatInTimezone(recording.startedAt, 'EEE, MM/dd, h:mm aa')}
              />
              <InfoField label="Pickup location" value={recording.pickupLocationName} />
              <InfoField label="Drop-off location" value={recording.dropoffLocationName} />
            </Box>
          </Box>

          <Alert
            severity="info"
            message="This recording contains Personally Identifiable Information (PII) for minor rider(s) and the CareDriver. This recording may only be used for its intended purpose in accordance with HopSkipDrive data policies."
          />

          <Box>
            <Box mb="12px">
              <InputLabel fieldtype="required" htmlFor="reason">
                Select download reason
              </InputLabel>
            </Box>
            <Box display="flex" flexDirection="column" gap="12px">
              {DOWNLOAD_REASONS.map(({ value, label, subtext }) => (
                <OptionCard
                  key={value}
                  value={value}
                  label={label}
                  subtext={subtext}
                  selected={reason === value}
                  onClick={setReason}
                >
                  {value === 'other' && (
                    <Box mt="6px">
                      <TextField
                        size="small"
                        variant="outlined"
                        fullWidth
                        placeholder="Describe your reason"
                        value={reasonDetail}
                        onChange={(e) => setReasonDetail(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid="download-reason-detail"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '22px',
                            height: '38px',
                            backgroundColor: 'white',
                          },
                        }}
                      />
                    </Box>
                  )}
                </OptionCard>
              ))}
            </Box>
          </Box>
        </Box>
      </Modal.Content>

      <Modal.Actions>
        <Button variant="text" onClick={handleClose} sx={{ color: colorPalette.navy30 }}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleDownload}
          disabled={isDownloadDisabled}
          loading={isSubmitting}
          data-testid="download-recording-submit"
        >
          Download
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
